import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { deleteDefaultQueryStateFromTable } from 'components/table/utlil';
import {
  closeModal,
  initialQueryState,
  openModal,
  showToastMessage,
  updateQueryStateTable,
  useTableStore,
} from 'enable-ui';

import { MODAL_KEY } from 'modules/auth/core/modal-key';

import type { Classes } from '../type';

import { getClassPath, trimValue } from 'helpers';
import { useNumber, useString } from 'hooks';
import { apiCall } from 'hooks/service';
import { debounce, isUndefined } from 'lodash';

import { PATH } from 'constants/path';
import { TABLE_KEY } from 'constants/table';

export const useClassesController = () => {
  const navigateTo = useNavigate();

  const queryState = useTableStore((s) => s.state.queryState[TABLE_KEY.CLASSES]);

  const [classes, setClasses] = useState<Classes[]>([]);

  const total = useNumber(0);

  const searchValue = useString('');

  const classSelected = useString('');

  useEffect(() => {
    updateQueryStateTable(TABLE_KEY.CLASSES, initialQueryState);

    return () => {
      updateQueryStateTable(TABLE_KEY.CLASSES, initialQueryState);
    };
  }, []);

  useEffect(() => {
    if (queryState) {
      getList();
    }
  }, [JSON.stringify(queryState)]);

  const getList = async (search: string = '') => {
    const result: { items: Classes[]; total: number } = await apiCall({
      url: '/classes',
      method: 'GET',
      isLoading: true,
      showError: true,
      data: {
        ...deleteDefaultQueryStateFromTable(queryState),
        search: search,
      },
    });

    setClasses(result?.items ?? []);

    if (!isUndefined(result?.total)) {
      total.setValue(result.total);
    }
  };

  const goToCreatePage = () => {
    navigateTo(`${PATH.CLASSES}/${PATH.CLASSES_ADD}`);
  };

  const onClickRow = (row: any) => {
    const original: Classes = row.original;

    if (original?.id) {
      navigateTo(getClassPath(original.id, PATH.CLASSES_EDIT));
    }
  };

  const onChangeSearchValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    searchValue.setValue(event.target.value);
    debounceSearchValue(event.target.value);
  };

  const debounceSearchValue = useCallback(
    debounce((newSearch: string) => {
      getList(trimValue(newSearch));
    }, 500),
    [],
  );

  const onClickAction = async (key: 'delete' | 'update-status', classId: string) => {
    switch (key) {
      case 'delete':
        classSelected.setValue(classId);
        openModal(MODAL_KEY.DELETE_CLASS);
        break;

      case 'update-status':
        await apiCall({
          url: `/classes/${classId}/status`,
          method: 'PATCH',
          isLoading: true,
          showError: true,
        });

        showToastMessage('success', `Updated class's status successfully`);

        getList();

        break;
    }
  };

  const handleDelete = async () => {
    await apiCall({
      url: `/classes/${classSelected.value}`,
      method: 'DELETE',
      isLoading: true,
      showError: true,
    });

    closeModal(MODAL_KEY.DELETE_CLASS);
    getList();
    showToastMessage('success', 'Removed class successfully!');
  };

  return {
    queryState,
    total,
    classes,
    goToCreatePage,
    onClickRow,
    searchValue,
    onChangeSearchValue,
    onClickAction,
    handleDelete,
  };
};
